// src/pages/transactions/TransactionDetailsPage.js

import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { HiArrowLeft, HiPrinter } from 'react-icons/hi2';

import { AuthContext } from '../../contexts/AuthContext';
import useApi from '../../hooks/useApi';
import Layout from '../../components/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import { BASE_URL } from '../../config/api';

const InfoRow = ({ label, value, className = '' }) => (
  <div className="flex justify-between py-2 border-b border-gray-200">
    <span className="text-sm font-medium text-gray-500">{label}</span>
    <span className={`text-sm text-gray-800 ${className}`}>{value}</span>
  </div>
);

const TransactionDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { settings, token } = useContext(AuthContext);

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const currencySymbol = settings?.currencySymbol || '$';

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        setLoading(true);
        const response = await api.get(`transactions/${id}`);
        setTransaction(response.data);
      } catch (err) {
        setError(err.message || 'Failed to fetch transaction details.');
      } finally {
        setLoading(false);
      }
    };
    fetchTransaction();
  }, [id, api]);

  const handlePrintInvoice = async () => {
    setIsPrinting(true);
    try {
      const response = await fetch(`${BASE_URL}/invoice/${id}/pdf?paperSize=a4`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF invoice.');
      }

      const pdfBlob = await response.blob();
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      URL.revokeObjectURL(pdfUrl);

    } catch (err) {
      console.error("Print error:", err);
      alert(err.message || "Could not print the invoice.");
    } finally {
      setIsPrinting(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${statusStyles[status] || statusStyles.pending}`}>
        {status}
      </span>
    );
  };

  if (loading) return <Layout><LoadingSpinner /></Layout>;
  if (error) return <Layout><ErrorMessage message={error} /></Layout>;
  if (!transaction) return <Layout><p>Transaction not found.</p></Layout>;

  return (
    <Layout title="Transaction Details">
      <div className="max-w-4xl mx-auto">
        {/* Header with Actions */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate('/transactions')}
            className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <HiArrowLeft className="w-5 h-5" />
            <span>Back to Transactions</span>
          </button>
          <button
            onClick={handlePrintInvoice}
            disabled={isPrinting}
            className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition duration-200 disabled:bg-indigo-300"
          >
            <HiPrinter className="w-5 h-5" />
            <span>{isPrinting ? 'Generating...' : 'Print Invoice'}</span>
          </button>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="md:col-span-2 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Items Purchased</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="text-left text-sm font-medium text-gray-500 py-2">Item</th>
                    <th className="text-center text-sm font-medium text-gray-500 py-2">Qty</th>
                    <th className="text-right text-sm font-medium text-gray-500 py-2">Price</th>
                    <th className="text-right text-sm font-medium text-gray-500 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {transaction.items.map((item) => (
                    <tr key={item._id} className="border-b border-gray-100">
                      <td className="py-3 font-medium text-gray-800">{item.productId?.name || item.productId}</td>
                      <td className="py-3 text-center text-gray-600">{item.quantity}</td>
                      <td className="py-3 text-right text-gray-600">{currencySymbol}{item.priceAtSale.toFixed(2)}</td>
                      <td className="py-3 text-right font-semibold text-gray-800">{currencySymbol}{(item.quantity * item.priceAtSale).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
              <InfoRow label="Transaction ID" value={transaction._id.substring(0, 12) + '...'} />
              <InfoRow label="Date" value={format(new Date(transaction.transactionDate), 'MMM d, yyyy, h:mm a')} />
              <InfoRow label="Status" value={<StatusBadge status={transaction.status} />} />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment</h2>
              <InfoRow label="Subtotal" value={`${currencySymbol}${transaction.totalAmount.toFixed(2)}`} />
              <InfoRow label="Taxes" value={`${currencySymbol}0`} />
              <InfoRow label="Total" value={`${currencySymbol}${transaction.totalAmount.toFixed(2)}`} className="font-bold text-lg text-indigo-600" />
              <InfoRow label="Payment Method" value={transaction.paymentMethod} />
            </div>
            {transaction.customer && (
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer</h2>
                <InfoRow label="Name" value={transaction.customer.name} />
                <InfoRow label="Email" value={transaction.customer.email} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TransactionDetailsPage;
